import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import StatTile from '../ui/StatTile'
import TimeChart, { fillMonthGaps } from '../ui/TimeChart'
import FunnelChart from '../ui/FunnelChart'
import RankChart from '../ui/RankChart'
import './analytics.css'

/*
 * ANALYTICS — the reporting screen.
 *
 * GET admin/analytics/overview
 * GET admin/analytics/growth?months=N     (N clamped 1..36 server-side)
 * GET admin/analytics/revenue?months=N
 * GET admin/analytics/breakdown
 *
 * All four are independent requests behind the same admin bearer token, and
 * they are kept independent on purpose: each panel group renders its own
 * skeleton, its own empty state and its own inline error with a retry, so one
 * dead endpoint costs one panel rather than the whole page.
 *
 * Two things about the payloads that the code below never assumes away:
 *
 *   1. Several numbers arrive as STRINGS ("403", "288"). The chart primitives
 *      coerce internally, so raw rows go straight to them; everything this
 *      file does arithmetic on goes through toNum() first.
 *   2. Months with no activity are ABSENT ROWS, not zero rows. A line drawn
 *      straight across a hole lies about the shape, so every time series is
 *      passed through fillMonthGaps() and pinned to a window that ends at the
 *      current month — that is what makes a stopped series read as stopped.
 *
 * The charts are the shared primitives (TimeChart / FunnelChart / RankChart);
 * none of them is re-implemented here and none of them is handed a colour that
 * is not a token name.
 */

/* ------------------------------------------------------------------
   NUMBERS
   ------------------------------------------------------------------ */
function toNum(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function grouped(v) {
  return toNum(v).toLocaleString('el-GR')
}

function eur(v) {
  return toNum(v).toLocaleString('el-GR') + ' €'
}

/* Whole euros — for a derived average, where three decimal places would read
   as thousands under the el-GR separators. */
function eurWhole(v) {
  return toNum(v).toLocaleString('el-GR', { maximumFractionDigits: 0 }) + ' €'
}

/* 'YYYY-MM' for the month we are in now. */
function currentYm() {
  const d = new Date()
  const m = d.getMonth() + 1
  return d.getFullYear() + '-' + (m < 10 ? '0' + m : String(m))
}

/* Position of a 'YYYY-MM' stamp on a continuous month scale, or null. */
function ymIndex(v) {
  const m = /^(\d{4})-(\d{1,2})/.exec(String(v === null || v === undefined ? '' : v))
  if (!m) return null
  return Number(m[1]) * 12 + (Number(m[2]) - 1)
}

/* The most the server's month boundary and ours can legitimately differ by.
   Two whole buckets is already generous for a timezone and a month-end. It
   bounds the widening below, so the period control keeps meaning what its
   label says: asking for 24 months can never draw a year and a half more. */
const WINDOW_SLACK = 2

/* How many buckets the window actually needs.
   The control asks the server for N months. The server counts its N back
   from its own clock; we count ours back from the browser's. The two windows
   can therefore disagree at the far edge — and they do: months=24 returns a
   row for 2024-09, while 24 buckets ending at the current month start at
   2024-10. Clipping to a flat N drops that row, and with it a real paid
   month, with nothing on screen to say a figure was cut.
   So N is a floor rather than a ceiling: widen until the window covers every
   row the response carried, up to the slack above. Months the API did not
   send are still zero-filled by fillMonthGaps, so widening adds honest zeros
   and never a fabricated reading. */
function windowMonths(rows, months, end) {
  const want = toNum(months) > 0 ? toNum(months) : 12
  const hi = ymIndex(end)
  if (hi === null) return want

  let lo = null
  for (let i = 0; i < rows.length; i += 1) {
    const n = ymIndex(rows[i] && rows[i].ym)
    if (n === null) continue
    if (lo === null || n < lo) lo = n
  }
  if (lo === null || lo > hi) return want

  return Math.min(want + WINDOW_SLACK, Math.max(want, hi - lo + 1))
}

/* The window always ends at the current month, so months with no activity at
   the end of the range are drawn as the zeros they are instead of being cut
   off at the last row the API happened to return. A row from the future (a
   clock skew, an import) still wins, so nothing is ever dropped. */
function windowEnd(rows) {
  const last = rows.length > 0 ? String(rows[rows.length - 1].ym || '') : ''
  const now = currentYm()
  return last > now ? last : now
}

/* Data values, not UI copy: the API labels an unset field 'Not set'. The one
   English value on an otherwise Greek screen is spelled out. Blank values are
   left blank — RankChart already renders those as '—'. */
const VALUE_LABELS = { 'Not set': 'Δεν έχει οριστεί' }

function labelOf(v) {
  const s = String(v === null || v === undefined ? '' : v).trim()
  if (s.length === 0) return ''
  return VALUE_LABELS[s] || s
}

const GROWTH_KEYS = ['registrations', 'activeUsers', 'logins', 'posts', 'challenges']
const REVENUE_KEYS = ['paidOrders', 'abandonedOrders', 'revenue', 'lost']

/* ------------------------------------------------------------------
   ONE ENDPOINT, ONE PANEL GROUP
   `path` carries the query string, so changing the period simply changes the
   path and the effect refetches. A response that is not err:0 is a failure —
   the panel says so and offers the retry rather than drawing an empty chart
   that looks like a real zero.
   ------------------------------------------------------------------ */
function useAnalytics(path) {
  const [state, setState] = useState({ data: null, loading: true, failed: false })
  const [nonce, setNonce] = useState(0)

  const retry = useCallback(() => setNonce((v) => v + 1), [])

  useEffect(() => {
    let alive = true
    setState((prev) => ({ data: prev.data, loading: true, failed: false }))

    axios.get(process.env.REACT_APP_API_SERVER + path, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then((res) => {
        if (!alive) return
        const body = res && res.data
        if (!body || Number(body.err) !== 0 || !body.results) {
          setState({ data: null, loading: false, failed: true })
          return
        }
        setState({ data: body.results, loading: false, failed: false })
      })
      .catch((err) => {
        console.log(err)
        if (alive) setState({ data: null, loading: false, failed: true })
      })

    return () => { alive = false }
  }, [path, nonce])

  return { data: state.data, loading: state.loading, failed: state.failed, retry }
}

/* ------------------------------------------------------------------
   PIECES
   ------------------------------------------------------------------ */
function ErrNote({ what, onRetry }) {
  return (
    <div className="an-note" role="alert">
      <span className="an-note-text">
        <b>{what} δεν φόρτωσαν.</b> Ο διακομιστής δεν απάντησε. Τα υπόλοιπα στοιχεία
        της σελίδας δεν επηρεάζονται.
      </span>
      <Btn onClick={onRetry}>Δοκιμή ξανά</Btn>
    </div>
  )
}

function TileSkeleton() {
  return (
    <div className="stat an-stat-sk" aria-hidden="true">
      <span className="skeleton an-sk-label"></span>
      <span className="skeleton an-sk-value"></span>
      <span className="skeleton an-sk-note"></span>
    </div>
  )
}

function SectionHead({ title, note }) {
  return (
    <div className="an-sec">
      <h2 className="an-sec-title">{title}</h2>
      {note ? <span className="an-sec-note">{note}</span> : null}
    </div>
  )
}

/* ------------------------------------------------------------------
   SCREEN
   ------------------------------------------------------------------ */
export default function Analytics() {

  const [months, setMonths] = useState(12)

  const overview = useAnalytics('admin/analytics/overview')
  const growth = useAnalytics('admin/analytics/growth?months=' + months)
  const revenue = useAnalytics('admin/analytics/revenue?months=' + months)
  const breakdown = useAnalytics('admin/analytics/breakdown')

  const o = overview.data || {}

  /* ---------------- growth ---------------- */
  const growthRows = useMemo(() => {
    const rows = Array.isArray(growth.data) ? growth.data : []
    if (rows.length === 0) return []
    const end = windowEnd(rows)
    return fillMonthGaps(rows, {
      xKey: 'ym',
      months: windowMonths(rows, months, end),
      end: end,
      keys: GROWTH_KEYS
    })
  }, [growth.data, months])

  /* ---------------- revenue ---------------- */
  const rev = revenue.data || {}

  const revenueRows = useMemo(() => {
    const rows = Array.isArray(rev.byMonth) ? rev.byMonth : []
    if (rows.length === 0) return []
    const end = windowEnd(rows)
    return fillMonthGaps(rows, {
      xKey: 'ym',
      months: windowMonths(rows, months, end),
      end: end,
      keys: REVENUE_KEYS
    })
  }, [rev.byMonth, months])

  const productRows = useMemo(() => {
    const rows = Array.isArray(rev.byProduct) ? rev.byProduct : []
    return rows.map((row) => ({
      ...row,
      anLabel: [row.Title, row.SportName].filter(Boolean).join(' · ')
    }))
  }, [rev.byProduct])

  /* ---------------- funnel ----------------
     No `funnel` object means no funnel: hand FunnelChart an empty list so it
     shows its empty state, rather than five bars of zero that look measured. */
  const funnelSteps = useMemo(() => {
    const f = (overview.data && overview.data.funnel) || null
    if (!f) return []
    return [
      { label: 'Εγγραφές', value: f.registered },
      { label: 'Πρώτη σύνδεση', value: f.loggedIn },
      { label: 'Επιστροφή', value: f.returned },
      { label: 'Δημοσίευση', value: f.posted },
      { label: 'Συμμετοχή σε challenge', value: f.enteredChallenge }
    ]
  }, [overview.data])

  /* ---------------- breakdown ---------------- */
  const brk = breakdown.data || {}

  const countryRows = useMemo(() => {
    const rows = Array.isArray(brk.byCountry) ? brk.byCountry : []
    return rows.map((row) => ({ ...row, anLabel: labelOf(row.Country) }))
  }, [brk.byCountry])

  const sportRows = useMemo(() => {
    const rows = Array.isArray(brk.bySport) ? brk.bySport : []
    return rows.map((row) => ({ ...row, anLabel: labelOf(row.SportName) }))
  }, [brk.bySport])

  const positionRows = useMemo(() => {
    const rows = Array.isArray(brk.byPosition) ? brk.byPosition : []
    return rows.map((row) => ({ ...row, anLabel: labelOf(row.position) }))
  }, [brk.byPosition])

  /* Factual captions computed from the rows themselves — no targets, no
     benchmarks, no causes. */
  const positionCap = useMemo(() => {
    const rows = Array.isArray(brk.byPosition) ? brk.byPosition : []
    if (rows.length === 0) return ''
    let total = 0
    let unset = 0
    rows.forEach((row) => {
      const users = toNum(row.users)
      total += users
      const raw = String(row.position === null || row.position === undefined ? '' : row.position).trim()
      if (raw.length === 0 || raw === 'Not set') unset += users
    })
    if (total <= 0) return ''
    return 'Θέση δηλωμένη στο προφίλ. ' + grouped(unset) + ' από ' + grouped(total) +
      ' χρήστες δεν έχουν ορίσει θέση.'
  }, [brk.byPosition])

  const countryCap = useMemo(() => {
    const rows = Array.isArray(brk.byCountry) ? brk.byCountry : []
    if (rows.length === 0) return ''
    return 'Χρήστες ανά χώρα, από ' + grouped(rows.length) + ' χώρες συνολικά.'
  }, [brk.byCountry])

  const period = months === 24 ? 'τελευταίοι 24 μήνες' : 'τελευταίοι 12 μήνες'

  /* ---------------- KPI row ---------------- */
  const kpis = overview.loading && !overview.data
    ? (
      <div className="sb-grid">
        <TileSkeleton />
        <TileSkeleton />
        <TileSkeleton />
        <TileSkeleton />
      </div>
    )
    : (
      <div className="sb-grid">
        <StatTile
          index={0}
          label="Σύνολο χρηστών"
          value={toNum(o.totalUsers)}
          note={<>Λογαριασμοί συνολικά · ενεργοί <b>{grouped(o.activeUsers)}</b></>}
        />
        <StatTile
          index={1}
          label="MAU"
          value={toNum(o.mau)}
          note={<>Χρήστες με δραστηριότητα 30 ημερών · WAU <b>{grouped(o.wau)}</b> · DAU <b>{grouped(o.dau)}</b></>}
        />
        <StatTile
          index={2}
          label="Νέες εγγραφές μήνα"
          value={toNum(o.newThisMonth)}
          note={<>Τον προηγούμενο μήνα <b>{grouped(o.newLastMonth)}</b></>}
        />
        <StatTile
          index={3}
          label="Ενεργές συνδρομές"
          value={toNum(o.activeSubscriptions)}
          note={<>Σε ισχύ σήμερα · λήγουν σύντομα <b>{grouped(o.expiringSoon)}</b></>}
        />
      </div>
    )

  return (
    <div className="an-page">

      {/* ---------------------------------------------------------------
          1. PERIOD — the one control on the page. It says what it drives.
          --------------------------------------------------------------- */}
      <div className="an-head">
        <div className="an-head-text">
          <span className="mono an-eyebrow">Περίοδος</span>
          <p className="an-head-sub">
            Ορίζει το εύρος στα διαγράμματα ανάπτυξης και εσόδων. Τα υπόλοιπα
            πάνελ δείχνουν πάντα το σύνολο.
          </p>
        </div>

        <div className="an-seg" role="group" aria-label="Περίοδος αναφοράς">
          <button
            type="button"
            className="toggle"
            aria-pressed={months === 12}
            onClick={() => setMonths(12)}
          >
            12 μήνες
          </button>
          <button
            type="button"
            className="toggle"
            aria-pressed={months === 24}
            onClick={() => setMonths(24)}
          >
            24 μήνες
          </button>
        </div>
      </div>

      {/* ---------------------------------------------------------------
          2. KPI ROW
          --------------------------------------------------------------- */}
      {overview.failed ? (
        <Panel title="Βασικά μεγέθη" subtitle="Χρήστες, δραστηριότητα και συνδρομές">
          <ErrNote what="Τα βασικά μεγέθη" onRetry={overview.retry} />
        </Panel>
      ) : (
        <div className="scoreboard an-kpis">{kpis}</div>
      )}

      {/* ---------------------------------------------------------------
          3. GROWTH
          Registrations and monthly actives are both counts of PEOPLE, so
          they share an axis. Logins count EVENTS and run an order of
          magnitude higher — its own chart, its own axis.
          --------------------------------------------------------------- */}
      <SectionHead title="Ανάπτυξη" note={period} />

      {growth.failed ? (
        <Panel title="Ανάπτυξη ανά μήνα" subtitle={period}>
          <ErrNote what="Τα δεδομένα ανάπτυξης" onRetry={growth.retry} />
        </Panel>
      ) : (
        <>
          <Panel
            title="Χρήστες ανά μήνα"
            subtitle="Νέες εγγραφές και μηνιαίοι ενεργοί χρήστες"
          >
            <TimeChart
              data={growthRows}
              series={[
                { key: 'registrations', label: 'Εγγραφές', color: '--mark' },
                { key: 'activeUsers', label: 'Ενεργοί χρήστες', color: '--good' }
              ]}
              xKey="ym"
              height={240}
              loading={growth.loading}
              empty="Δεν υπάρχουν εγγραφές ή δραστηριότητα σε αυτή την περίοδο."
              caption="Εγγραφές: νέοι λογαριασμοί μέσα στον μήνα. Ενεργοί χρήστες: όσοι είχαν δραστηριότητα μέσα στον ίδιο μήνα."
            />
          </Panel>

          <div className="grid grid-halves">
            <Panel title="Συνδέσεις ανά μήνα" subtitle="Σύνολο συνδέσεων">
              <TimeChart
                data={growthRows}
                series={[{ key: 'logins', label: 'Συνδέσεις', color: '--mark' }]}
                xKey="ym"
                height={210}
                loading={growth.loading}
                empty="Δεν καταγράφηκαν συνδέσεις σε αυτή την περίοδο."
                caption="Μετρά γεγονότα σύνδεσης, όχι πρόσωπα — γι' αυτό είναι σε χωριστό άξονα από τους χρήστες."
              />
            </Panel>

            <Panel
              title="Περιεχόμενο ανά μήνα"
              subtitle={overview.data
                ? 'Σύνολο: ' + grouped(o.totalPosts) + ' δημοσιεύσεις · ' +
                  grouped(o.totalChallengeEntries) + ' συμμετοχές'
                : 'Δημοσιεύσεις και συμμετοχές σε challenges'}
            >
              <TimeChart
                data={growthRows}
                series={[
                  { key: 'posts', label: 'Δημοσιεύσεις', color: '--mark' },
                  { key: 'challenges', label: 'Συμμετοχές', color: '--good' }
                ]}
                xKey="ym"
                height={210}
                loading={growth.loading}
                empty="Δεν δημοσιεύτηκε περιεχόμενο σε αυτή την περίοδο."
                caption="Δημοσιεύσεις χρηστών και συμμετοχές σε challenges, ανά μήνα δημιουργίας."
              />
            </Panel>
          </div>
        </>
      )}

      {/* ---------------------------------------------------------------
          4. THE ACTIVATION FUNNEL
          Full width and first after growth: every bar is measured against
          step one, which is what makes the shape readable.
          --------------------------------------------------------------- */}
      <SectionHead title="Χοάνη ενεργοποίησης" note="σύνολο, όλης της διάρκειας" />

      {overview.failed ? (
        <Panel title="Από την εγγραφή στη συμμετοχή">
          <ErrNote what="Τα δεδομένα της χοάνης" onRetry={overview.retry} />
        </Panel>
      ) : (
        <Panel
          className="an-funnel"
          title="Από την εγγραφή στη συμμετοχή"
          subtitle="Πόσοι από τους εγγεγραμμένους φτάνουν σε κάθε βήμα"
        >
          <FunnelChart
            steps={funnelSteps}
            pctLabel="% των εγγραφών"
            loading={overview.loading}
            empty="Δεν υπάρχουν δεδομένα χοάνης."
            caption="Κάθε ποσοστό μετριέται πάνω στο σύνολο των εγγραφών, όχι πάνω στο προηγούμενο βήμα. Η μείωση δίπλα σε κάθε βήμα είναι η διαφορά από το προηγούμενο."
          />
        </Panel>
      )}

      {/* ---------------------------------------------------------------
          5. REVENUE
          Euros and order counts are different measures — two charts, never
          two axes on one.
          --------------------------------------------------------------- */}
      <SectionHead title="Έσοδα" note={period} />

      {!overview.failed && (
        <div className="scoreboard an-figs">
          {overview.loading && !overview.data ? (
            <div className="sb-grid">
              <TileSkeleton />
              <TileSkeleton />
              <TileSkeleton />
            </div>
          ) : (
            <div className="sb-grid">
              <StatTile
                index={0}
                label="Συνολικά έσοδα"
                value={eur(o.revenueTotal)}
                note={<>Από <b>{grouped(o.paidOrders)}</b> πληρωμένες παραγγελίες, σε όλη τη διάρκεια</>}
              />
              <StatTile
                index={1}
                label="Εγκαταλελειμμένα καλάθια"
                value={eur(o.revenueAbandoned)}
                note={<><b>{grouped(o.abandonedOrders)}</b> παραγγελίες που ξεκίνησαν και δεν ολοκληρώθηκαν</>}
              />
              <StatTile
                index={2}
                label="Μέση αξία παραγγελίας"
                value={toNum(o.paidOrders) > 0
                  ? eurWhole(toNum(o.revenueTotal) / toNum(o.paidOrders))
                  : '—'}
                note={<>Συνολικά έσοδα διά το πλήθος των πληρωμένων παραγγελιών</>}
              />
            </div>
          )}
        </div>
      )}

      {revenue.failed ? (
        <Panel title="Έσοδα ανά μήνα" subtitle={period}>
          <ErrNote what="Τα οικονομικά στοιχεία" onRetry={revenue.retry} />
        </Panel>
      ) : (
        <>
          <div className="grid grid-halves">
            <Panel title="Έσοδα ανά μήνα" subtitle="Εισπραγμένα και χαμένα σε ευρώ">
              <TimeChart
                data={revenueRows}
                series={[
                  { key: 'revenue', label: 'Έσοδα', color: '--mark' },
                  { key: 'lost', label: 'Χαμένα σε εγκατάλειψη', color: '--crit' }
                ]}
                xKey="ym"
                height={210}
                loading={revenue.loading}
                format={(v) => Number(v).toLocaleString('el-GR') + ' €'}
                empty="Καμία πληρωμή σε αυτή την περίοδο."
                caption="Έσοδα: αξία των πληρωμένων παραγγελιών του μήνα. Χαμένα: αξία των παραγγελιών που δεν ολοκληρώθηκαν."
              />
            </Panel>

            <Panel title="Παραγγελίες ανά μήνα" subtitle="Πλήθος, όχι αξία">
              <TimeChart
                data={revenueRows}
                series={[
                  { key: 'paidOrders', label: 'Πληρωμένες', color: '--mark' },
                  { key: 'abandonedOrders', label: 'Εγκαταλελειμμένες', color: '--crit' }
                ]}
                xKey="ym"
                height={210}
                loading={revenue.loading}
                empty="Καμία παραγγελία σε αυτή την περίοδο."
                caption="Πλήθος παραγγελιών ανά μήνα. Σε χωριστό διάγραμμα από τα ευρώ, γιατί είναι άλλο μέγεθος."
              />
            </Panel>
          </div>

          <Panel title="Έσοδα ανά προϊόν" subtitle="Σε όλη τη διάρκεια">
            <RankChart
              rows={productRows}
              labelKey="anLabel"
              valueKey="revenue"
              unit="€"
              limit={8}
              loading={revenue.loading}
              empty="Καμία πληρωμένη παραγγελία."
              caption="Συνολικά έσοδα ανά προϊόν, από τις πληρωμένες παραγγελίες."
            />
          </Panel>
        </>
      )}

      {/* ---------------------------------------------------------------
          6. WHERE THE USERS ARE
          --------------------------------------------------------------- */}
      <SectionHead title="Κατανομή χρηστών" note="σύνολο εγγεγραμμένων" />

      {breakdown.failed ? (
        <Panel title="Κατανομή χρηστών">
          <ErrNote what="Οι κατανομές χρηστών" onRetry={breakdown.retry} />
        </Panel>
      ) : (
        <>
          <div className="grid grid-2">
            <Panel title="Χώρες" subtitle="Χρήστες ανά χώρα">
              <RankChart
                rows={countryRows}
                labelKey="anLabel"
                valueKey="users"
                limit={8}
                loading={breakdown.loading}
                empty="Δεν υπάρχουν χρήστες με δηλωμένη χώρα."
                caption={countryCap}
              />
            </Panel>

            <Panel title="Αθλήματα" subtitle="Χρήστες ανά άθλημα">
              <RankChart
                rows={sportRows}
                labelKey="anLabel"
                valueKey="users"
                loading={breakdown.loading}
                empty="Δεν υπάρχουν χρήστες με δηλωμένο άθλημα."
                caption="Το άθλημα που έχει επιλεγεί στο προφίλ του χρήστη."
              />
            </Panel>
          </div>

          <Panel title="Θέσεις" subtitle="Χρήστες ανά θέση στο γήπεδο">
            <RankChart
              rows={positionRows}
              labelKey="anLabel"
              valueKey="users"
              limit={8}
              loading={breakdown.loading}
              empty="Δεν υπάρχουν χρήστες με δηλωμένη θέση."
              caption={positionCap}
            />
          </Panel>
        </>
      )}

    </div>
  )
}
